// The game's language: one current Lang, the two dictionaries, t() and the plural rule. No library —
// dictionaries are typed objects (en.ts must carry every key of ru.ts, the compiler checks it).
// Core stays language-agnostic: it keeps ids, every display name and description is looked up here.
import { RU } from './ru.js';
import { EN } from './en.js';

export type Lang = 'ru' | 'en';
export const LANGS: readonly Lang[] = ['ru', 'en'];
export type MsgKey = keyof typeof RU;
export type Params = Record<string, string | number>;

export const DICTS: Record<Lang, Record<MsgKey, string>> = { ru: RU, en: EN };

let current: Lang = 'ru';
const listeners = new Set<(lang: Lang) => void>();

export const getLang = (): Lang => current;

export function setLang(lang: Lang) {
  if (lang === current) return;
  current = lang;
  for (const fn of [...listeners]) fn(lang);
}

/** Called after every language switch; returns the unsubscribe. */
export function onLangChange(fn: (lang: Lang) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** The player never chose: Russian for a browser that prefers ru/uk/be, English otherwise. */
export function detectLang(languages: readonly string[] | undefined): Lang {
  return (languages ?? []).some(l => /^(ru|uk|be)\b/i.test(l)) ? 'ru' : 'en';
}

/** Numbers print with at most two decimals, a decimal comma in Russian. */
export function fmtNum(x: number, lang: Lang = current): string {
  const s = String(Math.round(x * 100) / 100);
  return lang === 'ru' ? s.replace('.', ',') : s;
}

/** Russian: one / few / many (1 · 2–4 · 5+, with 11–14 as many); English: one / other. */
export function pluralForm(n: number, lang: Lang = current): 0 | 1 | 2 {
  const a = Math.abs(Math.trunc(n));
  if (lang === 'en') return a === 1 ? 0 : 1;
  const d = a % 10, dd = a % 100;
  if (d === 1 && dd !== 11) return 0;
  if (d >= 2 && d <= 4 && (dd < 12 || dd > 14)) return 1;
  return 2;
}

/** `{name}` inserts a param (numbers via fmtNum); `{name|one|few|many}` picks a plural form by that param
    (English uses the first two). */
export function format(template: string, params: Params | undefined, lang: Lang = current): string {
  if (!params) return template;
  return template.replace(/\{(\w+)(\|[^}]*)?\}/g, (whole, name: string, forms: string | undefined) => {
    const v = params[name];
    if (v === undefined) return whole;
    if (forms) {
      const list = forms.slice(1).split('|');
      return list[Math.min(pluralForm(Number(v), lang), list.length - 1)] ?? '';
    }
    return typeof v === 'number' ? fmtNum(v, lang) : v;
  });
}

export function t(key: MsgKey, params?: Params): string {
  return format(DICTS[current][key], params, current);
}

/** For ids that are not all in the dictionary (user-made maps, unknown server codes): the entry or null. */
export function tMaybe(key: string, params?: Params): string | null {
  const s = (DICTS[current] as Record<string, string>)[key];
  return s === undefined ? null : format(s, params, current);
}
